import { useEffect, useRef, useState } from "react";
import {
  Gift, Zap, ShieldCheck, Headphones, Trophy, Users, Gamepad2, Banknote,
  BadgeCheck, Lock, Scale,
} from "lucide-react";
import { resolveIcon } from "../../lib/iconMap";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";

/* ---- Hardcoded fallbacks (used only before settings load OR if admin clears the list) ---- */
const FALLBACK_TRUST = [
  { id: "t1", icon: "Gift",        title_full: "100% Welcome Bonus",  title_short: "100% Bonus",     sub: "On first deposit",     color: "#ffc933", enabled: true },
  { id: "t2", icon: "Zap",         title_full: "Instant Withdrawal",  title_short: "Instant Payout", sub: "Processed in minutes", color: "#19a55c", enabled: true },
  { id: "t3", icon: "Headphones",  title_full: "24/7 Live Support",   title_short: "24/7 Support",   sub: "Bangla & English",     color: "#3b82f6", enabled: true },
  { id: "t4", icon: "ShieldCheck", title_full: "100% Safe & Licensed",title_short: "100% Safe",      sub: "Anjouan licensed",     color: "#ec4899", enabled: true },
];

const FALLBACK_WINS = [
  { user: "Ra****l",  game: "Aviator",          amount: 45320 },
  { user: "Sh****im", game: "Fortune Tiger",    amount: 18940 },
  { user: "Nu****ha", game: "Super Ace",        amount: 27510 },
  { user: "Fa****l",  game: "Crazy Time",       amount: 84300 },
];

const FALLBACK_STATS = [
  { id: "s1", icon: "Users",     end: 92,   prefix: "",  suffix: "K+",   label: "Active Players",   color: "#19a55c", enabled: true },
  { id: "s2", icon: "Gamepad2",  end: 1500, prefix: "",  suffix: "+",    label: "Games & Slots",    color: "#ffc933", enabled: true },
  { id: "s3", icon: "Banknote",  end: 120,  prefix: "৳", suffix: " Cr+", label: "Payouts to Date",  color: "#3b82f6", enabled: true },
  { id: "s4", icon: "BadgeCheck",end: 72,   prefix: "",  suffix: "+",    label: "Top Providers",    color: "#ec4899", enabled: true },
];

const FALLBACK_WHY = [
  { id: "w1", icon: "ShieldCheck", title: "Licensed & Regulated", short: "Official Anjouan gaming license (ALSI-202410030-FI1).", full: "Operating under the Autonomous Island of Anjouan gaming license (ALSI-202410030-FI1) — fully regulated and compliant.", color: "#19a55c", enabled: true },
  { id: "w2", icon: "Zap",         title: "Instant Deposits",     short: "bKash, Nagad, Rocket & UPay credit instantly.",           full: "bKash, Nagad, Rocket and UPay credit instantly. Withdrawals settle in under 24 hours.",                                color: "#ffc933", enabled: true },
  { id: "w3", icon: "Scale",       title: "Certified Fair Play",  short: "Provider-level RNG from Evolution, Pragmatic, JILI + more.", full: "Every game uses provider-level RNG from Evolution, Pragmatic Play, JILI, PG Soft and more — independently audited.", color: "#3b82f6", enabled: true },
  { id: "w4", icon: "Lock",        title: "Bank-grade Security",  short: "256-bit SSL, encrypted wallets, 2FA-ready.",              full: "256-bit SSL, encrypted wallets and 2FA-ready logins keep your funds and data protected.",                              color: "#ec4899", enabled: true },
];

const FALLBACK_BADGES = [
  { id: "anjouan", icon: "ShieldCheck", label: "Licensed",     sub: "ALSI-202410030-FI1",  color: "#19a55c", text: "",    enabled: true },
  { id: "age",     icon: "",            label: "Adults Only",  sub: "Play responsibly",    color: "#ef4444", text: "18+", enabled: true },
  { id: "ssl",     icon: "Lock",        label: "256-bit SSL",  sub: "Encrypted wallet",    color: "#3b82f6", text: "",    enabled: true },
  { id: "fair",    icon: "Scale",       label: "Responsible",  sub: "Fair & transparent",  color: "#ffc933", text: "",    enabled: true },
  { id: "support", icon: "Headphones",  label: "24/7 Support", sub: "Bangla & English",    color: "#ec4899", text: "",    enabled: true },
];

const enabled = (arr) => (Array.isArray(arr) ? arr.filter((x) => x.enabled !== false) : []);

/* =============================================================
   TrustStrip
   ============================================================= */
export const TrustStrip = () => {
  const { settings } = useSiteSettings() || {};
  if (settings?.home_trust_enabled === false) return null;
  const items = enabled(settings?.home_trust_items?.length ? settings.home_trust_items : FALLBACK_TRUST);
  if (!items.length) return null;
  return (
    <section
      data-testid="home-trust-strip"
      className="mt-3 sm:mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3"
    >
      {items.map((it, i) => {
        const Icon = resolveIcon(it.icon, Gift);
        return (
          <div
            key={it.id || i}
            data-testid={`trust-item-${i}`}
            className="group relative rounded-xl bg-gradient-to-br from-[#121a26] to-[#0b121b] border border-white/5 hover:border-[#19a55c]/40 transition-all hover:-translate-y-0.5 overflow-hidden"
          >
            <div
              aria-hidden
              className="h-0.5 w-full"
              style={{ background: `linear-gradient(90deg, transparent, ${it.color}, transparent)` }}
            />
            <div
              aria-hidden
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              style={{ background: `radial-gradient(120% 100% at 0% 0%, ${it.color}1f, transparent 65%)` }}
            />
            <div className="relative flex items-center gap-2.5 sm:gap-3 px-3 py-3 sm:px-4 sm:py-3.5">
              <div
                className="shrink-0 h-9 w-9 sm:h-11 sm:w-11 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
                style={{ background: `${it.color}1a`, boxShadow: `inset 0 0 0 1px ${it.color}50` }}
              >
                <Icon size={18} style={{ color: it.color }} className="sm:hidden" />
                <Icon size={20} style={{ color: it.color }} className="hidden sm:block" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] sm:text-sm font-bold text-white leading-tight">
                  <span className="sm:hidden">{it.title_short || it.title_full}</span>
                  <span className="hidden sm:inline">{it.title_full || it.title_short}</span>
                </div>
                <div className="text-[10px] sm:text-xs text-white/55 truncate mt-0.5">{it.sub}</div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};

/* =============================================================
   LiveWinsTicker
   ============================================================= */
const fmt = (n) => "৳" + Number(n || 0).toLocaleString("en-IN");
export const LiveWinsTicker = () => {
  const { settings } = useSiteSettings() || {};
  if (settings?.home_live_wins_enabled === false) return null;
  const pool = settings?.home_live_wins_items?.length ? settings.home_live_wins_items : FALLBACK_WINS;
  if (!pool.length) return null;
  const list = [...pool, ...pool];
  return (
    <section
      data-testid="home-live-wins"
      className="mt-3 sm:mt-5 rounded-xl border border-white/5 bg-gradient-to-r from-[#0d1621] via-[#0f1a27] to-[#0d1621] overflow-hidden shadow-[0_2px_20px_rgba(0,0,0,0.35)]"
    >
      <div className="flex items-stretch">
        <div className="shrink-0 flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-[#19a55c]/25 to-[#19a55c]/10 border-r border-[#19a55c]/30">
          <span className="relative inline-flex h-1.5 w-1.5 sm:h-2 sm:w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#19a55c] opacity-70 animate-ping" />
            <span className="relative inline-flex h-full w-full rounded-full bg-[#19a55c]" />
          </span>
          <span className="text-[10px] sm:text-xs font-extrabold tracking-wider text-[#19a55c] uppercase whitespace-nowrap">
            Live Wins
          </span>
        </div>
        <div className="flex-1 overflow-hidden relative">
          <div className="flex gap-4 sm:gap-8 py-2 sm:py-2.5 whitespace-nowrap animate-[ticker_25s_linear_infinite] will-change-transform hover:[animation-play-state:paused]">
            {list.map((w, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-sm">
                <Trophy size={11} className="text-[#ffc933] shrink-0 sm:hidden" />
                <Trophy size={13} className="text-[#ffc933] shrink-0 hidden sm:block" />
                <span className="text-white/85 font-semibold">{w.user}</span>
                <span className="text-white/35">won</span>
                <span className="text-[#19a55c] font-extrabold">{fmt(w.amount)}</span>
                <span className="text-white/35">on</span>
                <span className="text-white/65">{w.game}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};

/* =============================================================
   StatsCounter
   ============================================================= */
function useCountUp(target, { duration = 1600 } = {}) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(Math.round(target * eased));
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);
  return [ref, value];
}

const StatBlock = ({ iconName, end, prefix, suffix, label, color, testId }) => {
  const [ref, val] = useCountUp(Number(end || 0));
  const Icon = resolveIcon(iconName, BadgeCheck);
  return (
    <div
      ref={ref}
      data-testid={testId}
      className="group relative rounded-xl bg-gradient-to-br from-[#131c2a] to-[#0b121b] border border-white/5 hover:border-white/15 transition-all hover:-translate-y-0.5 p-3.5 sm:p-5 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-15 blur-3xl group-hover:opacity-25 transition-opacity"
        style={{ background: color }}
      />
      <div
        className="relative h-9 w-9 sm:h-11 sm:w-11 rounded-lg flex items-center justify-center mb-2 sm:mb-2.5 transition-transform group-hover:scale-110"
        style={{ background: `${color}1c`, boxShadow: `inset 0 0 0 1px ${color}55` }}
      >
        <Icon size={17} style={{ color }} className="sm:hidden" />
        <Icon size={20} style={{ color }} className="hidden sm:block" />
      </div>
      <div
        className="relative font-display text-[22px] sm:text-[32px] lg:text-[36px] font-extrabold text-white tracking-tight leading-none"
        style={{ letterSpacing: "-0.035em" }}
      >
        {prefix || ""}
        {val.toLocaleString("en-IN")}
        <span style={{ color }}>{suffix || ""}</span>
      </div>
      <div className="relative text-[10px] sm:text-[11px] text-white/45 uppercase tracking-[0.12em] font-semibold mt-1">
        {label}
      </div>
    </div>
  );
};

export const StatsCounter = () => {
  const { settings } = useSiteSettings() || {};
  if (settings?.home_stats_enabled === false) return null;
  const items = enabled(settings?.home_stats_items?.length ? settings.home_stats_items : FALLBACK_STATS);
  if (!items.length) return null;
  return (
    <section
      data-testid="home-stats-counter"
      className="mt-5 sm:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3"
    >
      {items.map((it, i) => (
        <StatBlock
          key={it.id || i}
          iconName={it.icon}
          end={it.end}
          prefix={it.prefix}
          suffix={it.suffix}
          label={it.label}
          color={it.color}
          testId={`stat-${it.id || i}`}
        />
      ))}
    </section>
  );
};

/* =============================================================
   WhyChooseUs
   ============================================================= */
export const WhyChooseUs = () => {
  const { settings } = useSiteSettings() || {};
  if (settings?.home_why_enabled === false) return null;
  const items = enabled(settings?.home_why_items?.length ? settings.home_why_items : FALLBACK_WHY);
  if (!items.length) return null;
  const brand = settings?.site_name || "Maxlive88";
  return (
    <section data-testid="home-why-choose-us" className="py-6 sm:py-10 border-t border-white/5 mt-5 sm:mt-8">
      <div className="flex items-center gap-2.5 sm:gap-3 mb-4 sm:mb-6">
        <span className="h-6 sm:h-7 w-1 sm:w-[5px] rounded-full bg-[#19a55c]" />
        <h2 className="text-lg sm:text-2xl font-bold text-white">
          Why <span className="text-[#19a55c]">{brand}</span>
        </h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        {items.map((it, i) => {
          const Icon = resolveIcon(it.icon, ShieldCheck);
          return (
            <div
              key={it.id || i}
              data-testid={`why-card-${i}`}
              className="group relative rounded-xl border border-white/5 bg-gradient-to-br from-[#121a26] to-[#0b121b] hover:border-white/15 transition-all hover:-translate-y-0.5 p-3 sm:p-5 overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -right-6 -top-6 h-16 w-16 sm:h-24 sm:w-24 rounded-full opacity-10 blur-2xl group-hover:opacity-25 transition-opacity"
                style={{ background: it.color }}
              />
              <div
                className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center mb-2.5 sm:mb-3 transition-transform group-hover:scale-110"
                style={{ background: `${it.color}20`, boxShadow: `inset 0 0 0 1px ${it.color}60` }}
              >
                <Icon size={18} style={{ color: it.color }} className="sm:hidden" />
                <Icon size={22} style={{ color: it.color }} className="hidden sm:block" />
              </div>
              <h4 className="relative text-[13.5px] sm:text-[16px] font-bold text-white mb-1 leading-tight">
                {it.title}
              </h4>
              <p className="relative text-[11px] sm:text-[13px] text-white/55 leading-relaxed">
                <span className="sm:hidden">{it.short || it.full}</span>
                <span className="hidden sm:inline">{it.full || it.short}</span>
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* =============================================================
   LicenseBadges
   ============================================================= */
export const LicenseBadges = () => {
  const { settings } = useSiteSettings() || {};
  if (settings?.home_license_enabled === false) return null;
  const badges = enabled(settings?.home_license_badges?.length ? settings.home_license_badges : FALLBACK_BADGES);
  if (!badges.length) return null;
  return (
    <section
      data-testid="home-license-badges"
      className="mt-5 sm:mt-8 rounded-2xl border border-white/5 bg-gradient-to-br from-[#0d141f] to-[#09101a] px-3 sm:px-6 py-3.5 sm:py-5"
    >
      <div className={`grid grid-cols-2 gap-2.5 sm:gap-4 sm:grid-cols-${Math.min(badges.length, 5)}`}>
        {badges.map((b, i) => {
          const Icon = b.icon ? resolveIcon(b.icon, ShieldCheck) : null;
          return (
            <div
              key={b.id || i}
              data-testid={`badge-${b.id || i}`}
              className={`flex items-center gap-2 sm:gap-2.5 ${
                // On mobile, if odd count → last item spans full width
                badges.length % 2 === 1 && i === badges.length - 1 ? "col-span-2 sm:col-span-1 justify-center sm:justify-start" : ""
              }`}
            >
              <div
                className="shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center font-extrabold text-[13px] sm:text-sm"
                style={{ background: `${b.color}18`, border: `1px solid ${b.color}40`, color: b.color }}
              >
                {Icon ? (
                  <>
                    <Icon size={16} className="sm:hidden" />
                    <Icon size={18} className="hidden sm:block" />
                  </>
                ) : (
                  b.text
                )}
              </div>
              <div className="leading-tight min-w-0">
                <div className="text-white font-bold text-[12px] sm:text-[13px] truncate">{b.label}</div>
                <div className="text-[10px] sm:text-[11px] text-white/45 truncate">{b.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
